import {
  type AnalyticsTraitsContext,
  useOptionalAnalytics,
} from '@repo/internal-tools-analytics/src/AnalyticsProvider.tsx'
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import type {
  FlattenedSettingValues,
  SettingItemConfigId,
  SettingsConfig,
} from './settingsConfigSchema.ts'
import { getSettingStateStore } from './settingStore.ts'

export type UseSettingItemOptions = {
  /**
   * By default, when the value is not set, it will return the default value.
   * Set this to true to return undefined instead.
   */
  disableDefaultValue?: boolean
  /**
   * Track this setting's evaluated state as an analytics event.
   *
   * This emits (deduped):
   * - `trackOnce({ event: 'settingEvaluated', key: '<settingsId>.<itemId>', properties: { component, value } })`
   *
   * `component` is namespaced by app to avoid collisions:
   * `<appId>.<settingsId>.<itemId>`
   *
   * Notes:
   * - `value` is stringified.
   * - When `AnalyticsProvider` is missing, this is a no-op (and logs an error).
   *
   * @default false
   */
  shouldTrackStateInAnalytics?: boolean
}

export type UseSettingItemValueReturn<
  RootConfig extends SettingsConfig,
  ItemId extends Extract<
    keyof FlattenedSettingValues<RootConfig>,
    SettingItemConfigId
  >,
  Options extends UseSettingItemOptions = UseSettingItemOptions,
> = Options['disableDefaultValue'] extends true
  ? FlattenedSettingValues<RootConfig>[ItemId]
  : NonNullable<FlattenedSettingValues<RootConfig>[ItemId]>

export const useSettingItemValue = <
  RootConfig extends SettingsConfig,
  ItemId extends Extract<
    keyof FlattenedSettingValues<RootConfig>,
    SettingItemConfigId
  >,
  Options extends UseSettingItemOptions = UseSettingItemOptions,
>(
  settingsConfig: RootConfig,
  itemId: ItemId,
  options?: Options,
): UseSettingItemValueReturn<RootConfig, ItemId, Options> => {
  const store = getSettingStateStore(settingsConfig)
  const analytics = useOptionalAnalytics()

  const getSnapshot = useCallback(
    () => store.getState()[itemId],
    [itemId, store],
  )

  const value = useSyncExternalStore(store.subscribe, getSnapshot)

  const itemConfig = store.itemConfigByIdMap[itemId]

  // `itemId` should always be valid since we use strict type checking,
  // and the itemConfigByIdMap is composed from the settingsConfig.
  // So there is no way this result in `undefined` during runtime
  // without having type error in build time.
  // This is just a sanity check in case an unknown/unhandled case happens.
  if (!itemConfig) {
    console.error('[useSettingItemValue] Item config not found for itemId:', {
      itemId,
      itemConfigByIdMap: store.itemConfigByIdMap,
    })
    throw new Error(
      `[useSettingItemValue] setting item config not found for id: "${String(itemId)}".`,
    )
  }

  const { disableDefaultValue, shouldTrackStateInAnalytics = false } =
    options ?? {}

  const defaultItemValue = itemConfig.default
  const evaluatedValue = (
    disableDefaultValue ? value : (value ?? defaultItemValue)
  ) as UseSettingItemValueReturn<RootConfig, ItemId, Options>

  // TODO: Reevaluate this if we can use useEffectEvent instead when upgrading to React 19.2
  useEffect(() => {
    if (!shouldTrackStateInAnalytics) return

    if (!analytics) {
      console.error(
        '[useSettingItemValue]: shouldTrackStateInAnalytics is true but no AnalyticsProvider was found.',
        { settingsId: settingsConfig.settingsId, itemId: String(itemId) },
      )
      return
    }
    const trackKey = `${settingsConfig.settingsId}.${itemId}`
    analytics.trackOnce({
      event: 'settingEvaluated',
      key: trackKey,
      properties: (ctx: AnalyticsTraitsContext) => ({
        component: `${ctx.appId}.${trackKey}`,
        value: String(evaluatedValue),
      }),
      // Only re-emit the event if the value changes.
      propsComparator: (prevProps, nextProps) => {
        return prevProps?.value === nextProps?.value
      },
    })
  }, [
    analytics,
    evaluatedValue,
    itemId,
    settingsConfig.settingsId,
    shouldTrackStateInAnalytics,
  ])

  return evaluatedValue
}

export const createUseSettingItemValueHook = <
  RootConfig extends SettingsConfig,
>(
  settingsConfig: RootConfig,
) => {
  return <
    ItemId extends Extract<
      keyof FlattenedSettingValues<RootConfig>,
      SettingItemConfigId
    >,
    Options extends UseSettingItemOptions = UseSettingItemOptions,
  >(
    itemId: ItemId,
    options?: Options,
  ) => {
    return useSettingItemValue<RootConfig, ItemId, Options>(
      settingsConfig,
      itemId,
      options,
    )
  }
}
